import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import { hasMesYatNativePrinter, printKitchenTicket } from "./mesyatPrinterAdapter";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://panel.haymestasarim.com/api";
const TOKEN_KEY = "mesyat_garson_token";
const STATION_KEY = "mesyat_mutfak_istasyon";
const DEVICE_KEY = "mesyat_mutfak_cihaz";

function deviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `device-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

async function api(path, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY) || "";
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.ok === false) {
    const error = new Error(json?.message || "İşlem tamamlanamadı");
    error.status = response.status;
    throw error;
  }
  return json;
}

function safePayload(job) {
  return job?.payload && typeof job.payload === "object" ? job.payload : {};
}

export default function MutfakYaziciPage() {
  const [bootstrap, setBootstrap] = useState(null);
  const [stationId, setStationId] = useState(
    localStorage.getItem(STATION_KEY) || ""
  );
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });
  const attemptedAutoPrint = useRef(new Set());

  const token = localStorage.getItem(TOKEN_KEY) || "";
  const stations = useMemo(
    () => (bootstrap?.stations || []).filter((station) => station.aktif !== false),
    [bootstrap]
  );
  const selectedStation = stations.find(
    (station) => String(station.id) === String(stationId)
  );

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, severity, message });
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    window.location.hash = "/garson";
  };

  const loadBootstrap = useCallback(async () => {
    if (!token) return;
    try {
      const json = await api("/garson/bootstrap?lang=tr");
      setBootstrap(json.data || null);
      const available = json.data?.stations || [];
      if (!stationId && available.length) {
        const first = available[0];
        setStationId(String(first.id));
        localStorage.setItem(STATION_KEY, String(first.id));
      }
    } catch (error) {
      if ([401, 403].includes(error.status)) logout();
      else notify(error.message, "error");
    }
  }, [notify, stationId, token]);

  const loadQueue = useCallback(
    async (silent = false) => {
      if (!token || !stationId) return;
      if (!silent) setLoading(true);
      try {
        const json = await api(
          `/garson/yazdirma-kuyrugu?durum=bekliyor&istasyon_id=${encodeURIComponent(
            stationId
          )}`
        );
        setJobs(json.data || []);
      } catch (error) {
        if ([401, 403].includes(error.status)) logout();
        else if (!silent) notify(error.message, "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [notify, stationId, token]
  );

  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  useEffect(() => {
    if (!stationId) return undefined;
    loadQueue(false);
    const timer = window.setInterval(() => loadQueue(true), 4000);
    return () => window.clearInterval(timer);
  }, [loadQueue, stationId]);

  const updateJob = async (job, status, extras = {}) => {
    await api(`/garson/yazdirma-kuyrugu/${job.id}/status`, {
      method: "PUT",
      body: JSON.stringify({
        durum: status,
        cihaz_id: deviceId(),
        ...extras,
      }),
    });
  };

  const printJob = useCallback(
    async (job, automatic = false) => {
      if (printingId) return;
      setPrintingId(job.id);
      try {
        await updateJob(job, "alindi");
        const result = await printKitchenTicket(job);
        await updateJob(job, "yazdirildi");
        setJobs((old) => old.filter((item) => item.id !== job.id));
        if (!automatic) {
          notify(
            result.mode === "browser"
              ? "Fiş yazdırma ekranına gönderildi"
              : "Fiş Bluetooth/yazıcı köprüsüne gönderildi"
          );
        }
      } catch (error) {
        await updateJob(job, "hata", { hata_mesaji: error.message }).catch(() => {});
        notify(error.message || "Fiş yazdırılamadı", "error");
      } finally {
        setPrintingId(null);
      }
    },
    [notify, printingId]
  );

  useEffect(() => {
    if (!autoPrint || !hasMesYatNativePrinter() || !jobs.length || printingId) return;
    const next = jobs.find((job) => !attemptedAutoPrint.current.has(job.id));
    if (!next) return;
    attemptedAutoPrint.current.add(next.id);
    printJob(next, true);
  }, [autoPrint, jobs, printJob, printingId]);

  if (!token) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", p: 2 }}>
        <Paper elevation={0} sx={{ maxWidth: 520, p: 3, border: "1px solid #e2e8f0", borderRadius: 4 }}>
          <Stack spacing={2} alignItems="center">
            <LocalPrintshopIcon sx={{ fontSize: 56, color: "#f97316" }} />
            <Typography variant="h5" sx={{ fontWeight: 950 }}>Mutfak Personel Girişi</Typography>
            <Alert severity="info">Mutfak ekranı için “Mutfak ekranı” yetkisi verilmiş personel PIN’iyle giriş yapın.</Alert>
          

<Button
  variant="contained"
  onClick={() => { window.location.hash = "/yazarkasa"; }}
>
  Yazar Kasa POS'a Git
</Button>


          </Stack>
        </Paper>
      </Box>
    );
  }

  if (bootstrap && bootstrap.profile?.mutfak_gorebilir !== true) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={logout}>Çıkış</Button>}>
          Bu personelin mutfak ve yazıcı ekranı yetkisi bulunmuyor.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "#f8fafc" }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: "#0f172a" }}>
        <Toolbar sx={{ gap: 1 }}>
          <LocalPrintshopIcon sx={{ color: "#f97316" }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 950 }}>MesYat Mutfak & Yazıcı</Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {bootstrap?.profile?.ad_soyad || "Mutfak Personeli"}
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={() => loadQueue(false)}><RefreshIcon /></IconButton>
          <IconButton color="inherit" onClick={logout}><LogoutIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 1400, mx: "auto", p: { xs: 1.3, md: 2.5 } }}>
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3.5, border: "1px solid #e2e8f0", mb: 1.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ sm: "center" }}>
            <TextField
              select
              label="Hazırlama istasyonu"
              value={stationId}
              onChange={(event) => {
                setStationId(event.target.value);
                localStorage.setItem(STATION_KEY, event.target.value);
              }}
              sx={{ minWidth: 260 }}
            >
              {stations.map((station) => <MenuItem key={station.id} value={String(station.id)}>{station.ad}</MenuItem>)}
            </TextField>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 900 }}>{selectedStation?.yazici_adi || "Sistem yazıcısı"}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedStation?.yazici_turu === "bluetooth" ? "Bluetooth" : selectedStation?.yazici_turu === "network" ? "Ağ yazıcısı" : "Tarayıcı/sistem yazdırma"} · {selectedStation?.kagit_genisligi || 80} mm
              </Typography>
            </Box>
            <FormControlLabel
              control={<Switch checked={autoPrint} onChange={(event) => setAutoPrint(event.target.checked)} disabled={!hasMesYatNativePrinter()} />}
              label="Otomatik yazdır"
            />
          </Stack>
        </Paper>

        {!hasMesYatNativePrinter() && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Native Bluetooth köprüsü bulunmadığı için fişler tarayıcının yazdırma ekranına gönderilecektir. MesYat uygulamasında köprü aktif olduğunda otomatik Bluetooth yazdırma açılır.
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Bekleyen Fişler</Typography>
          <Chip label={`${jobs.length} sipariş`} color={jobs.length ? "warning" : "success"} sx={{ fontWeight: 900 }} />
        </Stack>

        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}><CircularProgress /></Box>
        ) : !jobs.length ? (
          <Paper elevation={0} sx={{ p: 5, textAlign: "center", border: "1px solid #e2e8f0", borderRadius: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 62, color: "#16a34a" }} />
            <Typography variant="h5" sx={{ mt: 1, fontWeight: 950 }}>Bekleyen sipariş yok</Typography>
            <Typography color="text.secondary">Yeni siparişler otomatik olarak burada görünecek.</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))", xl: "repeat(3,minmax(0,1fr))" }, gap: 1.4 }}>
            {jobs.map((job) => {
              const payload = safePayload(job);
              const items = Array.isArray(payload.items) ? payload.items : [];
              return (
                <Card key={job.id} elevation={0} sx={{ border: "2px solid #fed7aa", borderRadius: 4, overflow: "hidden" }}>
                  <Box sx={{ bgcolor: "#fff7ed", p: 1.4 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 950 }}>{payload.table?.name || job.masa_no}</Typography>
                        <Typography variant="caption" color="text.secondary">Garson: {payload.waiter?.name || job.garson_adi}</Typography>
                      </Box>
                      <Chip label={`#${job.siparis_id}`} color="warning" sx={{ fontWeight: 950 }} />
                    </Stack>
                  </Box>
                  <CardContent>
                    <Stack spacing={1}>
                      {items.map((item, index) => (
                        <Box key={`${job.id}-${index}`}>
                          <Typography sx={{ fontSize: 17, fontWeight: 950 }}>
                            {item.quantity || 1} × {item.display_title || item.title}
                          </Typography>
                          {item.note && <Typography color="error" sx={{ fontWeight: 850 }}>Not: {item.note}</Typography>}
                          {index < items.length - 1 && <Divider sx={{ mt: 1 }} />}
                        </Box>
                      ))}
                      {payload.note && <Alert severity="warning"><strong>Sipariş Notu:</strong> {payload.note}</Alert>}
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={printingId === job.id ? <CircularProgress size={20} color="inherit" /> : <LocalPrintshopIcon />}
                        disabled={Boolean(printingId)}
                        onClick={() => printJob(job, false)}
                        sx={{ bgcolor: "#f97316", minHeight: 52, fontWeight: 950, textTransform: "none", "&:hover": { bgcolor: "#c2410c" } }}
                      >
                        Yazdır ve Tamamla
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((old) => ({ ...old, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} onClose={() => setSnack((old) => ({ ...old, open: false }))} sx={{ fontWeight: 850 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
